import React from 'react';
import { cn } from '../../lib/utils';

export interface DataCardField {
    label: string;
    value: React.ReactNode;
}

export interface DataCardModel {
    key: React.Key;
    /** Control rendered left of the header — a bulk-select checkbox, say. */
    leading?: React.ReactNode;
    /** Small badge/code line above the title — tags, IDs, source pills. */
    eyebrow?: React.ReactNode;
    /** Primary line. Keep it to the one field a user scans for. */
    title: React.ReactNode;
    /** Secondary line — the field that disambiguates two similar titles. */
    subtitle?: React.ReactNode;
    /**
     * Demoted columns as label/value pairs. Rendered outside the clickable
     * header, so a value may itself be interactive (a status toggle, a select).
     */
    fields?: DataCardField[];
    /** Row-level actions (an `ActionDropdown`, a toggle…). Kept reachable. */
    actions?: React.ReactNode;
    /** Row activation — becomes a real button so keyboard users get it too. */
    onClick?: () => void;
}

interface ResponsiveDataViewProps<T> {
    items: T[];
    /** Maps a row to its mobile card. See `DataCardModel` for the field roles. */
    toCard: (item: T) => DataCardModel;
    /** The desktop `<Table>`. Hidden (not just visually) below `md`. */
    children: React.ReactNode;
    /** Rendered in place of the card list when `items` is empty. */
    empty?: React.ReactNode;
    /** Applied to both the table wrapper and the card list (e.g. loading dim). */
    className?: string;
    /** Accessible name for the mobile list. */
    label?: string;
}

/**
 * Renders a wide data table two ways: the existing `<Table>` from `md` up, and
 * a stacked card list below it — one card per row, demoted columns as
 * label/value pairs.
 *
 * Why a card list rather than a horizontally-scrolling table: at 320–412px a
 * ten-column table shows two truncated columns and a scrollbar, and the sticky
 * uppercase header row eats a third of the screen for no information. The
 * header is genuinely useless on a phone, so it is removed from the flow
 * entirely — the whole desktop table is `display:none` below `md`, which also
 * means no orphaned `<th>` is left behind for a screen reader to announce out
 * of order. The card list carries its own labels in a `<dl>`.
 *
 * ## Applying this to another table
 *
 * 1. Wrap the existing `<Table>…</Table>` in `<ResponsiveDataView>` and move
 *    whatever wrapper classes it had onto `className`.
 * 2. Write `toCard`. Pick the roles deliberately:
 *    - `eyebrow`: the identifier and status pills — what the user scans first.
 *    - `leading`: the bulk-select checkbox, if the table has one.
 *    - `title`: the one column a user would search by (name, item, user).
 *    - `subtitle`: the disambiguator (email, model, category).
 *    - `fields`: everything else worth keeping. Drop columns that only make
 *      sense in comparison across rows; the row's detail view still has them.
 *    - `actions`: the row's `ActionDropdown` — never drop this.
 * 3. Pass `empty` so the empty state is shared, and delete the table's own
 *    "no results" `<TableRow>` only if it would otherwise double up.
 *
 * Do not reach for this on tables whose value *is* the cross-row comparison
 * (wide numeric grids); those should scroll inside their own wrapper instead.
 */
export function ResponsiveDataView<T>({
    items,
    toCard,
    children,
    empty,
    className,
    label,
}: ResponsiveDataViewProps<T>) {
    return (
        <>
            {/* Desktop (md+): the real table, scrolling inside its own wrapper. */}
            <div className={cn('hidden md:block', className)}>{children}</div>

            {/* Mobile (<md): stacked cards. */}
            <div className={cn('md:hidden', className)}>
                {items.length === 0
                    ? empty
                    : (
                        <ul className="divide-y divide-border" aria-label={label}>
                            {items.map((item) => {
                                // `key` is pulled out rather than spread through: React
                                // warns when a spread object still carries a `key`, even
                                // though the explicit prop is the one that wins.
                                const { key, ...card } = toCard(item);
                                return <DataCard key={key} {...card} />;
                            })}
                        </ul>
                    )}
            </div>
        </>
    );
}

const DataCard: React.FC<DataCardModel> = ({
    leading,
    eyebrow,
    title,
    subtitle,
    fields,
    actions,
    onClick,
}) => {
    const header = (
        <>
            {eyebrow && <div className="flex flex-wrap items-center gap-2">{eyebrow}</div>}
            <p className="mt-1 break-words font-medium">{title}</p>
            {subtitle && (
                <p className="break-words text-xs text-muted-foreground">{subtitle}</p>
            )}
        </>
    );

    return (
        <li className="px-4 py-3">
            <div className="flex items-start gap-3">
                {leading && <div className="flex shrink-0 items-center pt-1">{leading}</div>}
                {onClick ? (
                    <button
                        type="button"
                        onClick={onClick}
                        className="-mx-2 -my-1 min-w-0 flex-1 cursor-pointer rounded-md px-2 py-1 text-left active:bg-accent/50"
                    >
                        {header}
                    </button>
                ) : (
                    <div className="min-w-0 flex-1">{header}</div>
                )}
                {actions && (
                    <div className="flex shrink-0 items-center gap-1">{actions}</div>
                )}
            </div>
            {fields && fields.length > 0 && (
                <dl className="mt-2 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                    {fields.map((field) => (
                        <React.Fragment key={field.label}>
                            <dt className="text-muted-foreground">{field.label}</dt>
                            <dd className="min-w-0 break-words">{field.value}</dd>
                        </React.Fragment>
                    ))}
                </dl>
            )}
        </li>
    );
};
